import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { Loader2, LucideProps } from 'lucide-react';
import { ForwardRefExoticComponent, RefAttributes } from 'react';

const buttonVariants = cva(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
    {
        variants: {
            variant: {
                default:
                    'w-72 h-10 bg-gradient-to-tl from-purple-600 via-indigo-800 to-blue-750 rounded-[5px] border border-neutral-650 text-white hover:from-purple-700 hover:via-indigo-900 hover:to-blue-900 hover:border-neutral-700 data-[state=active]:from-purple-700 data-[state=active]:via-indigo-900 data-[state=active]:to-blue-900 data-[state=active]:border-neutral-700',
                destructive:
                    'bg-destructive text-destructive-foreground hover:bg-destructive/90 data-[state=active]:bg-destructive/90',
                outline:
                    'w-72 h-10 border border-white bg-transparent hover:bg-accent hover:text-accent-foreground data-[state=active]:bg-accent data-[state=active]:text-accent-foreground',
                secondary:
                    'bg-secondary text-secondary-foreground hover:bg-secondary/80 data-[state=active]:bg-secondary/80',
                locked: 'bg-neutral-600 text-neutral-300',
                'secondary-outline':
                    'h-10 border text-neutral-350 border-neutral-350 bg-neutral-550 hover:bg-neutral-500 data-[state=active]:bg-neutral-500 data-[state=active]:text-neutral-350 font-bold',
                ghost: 'hover:bg-accent hover:text-accent-foreground data-[state=active]:bg-accent data-[state=active]:text-accent-foreground',
                link: 'text-indigo-750 underline hover:text-indigo-900 data-[state=active]:text-indigo-900',
                'success-subtle':
                    'text-green-400 bg-green-900 bg-opacity-20 border border-green-400 border-opacity-20 hover:bg-opacity-40 hover:border-opacity-30',
                'danger-subtle':
                    'text-red-400 bg-red-900 bg-opacity-20 border border-red-400 border-opacity-20 hover:bg-opacity-40 hover:border-opacity-30',
            },
            size: {
                default: 'h-10 px-4',
                xs: 'h-6 px-1 py-0 text-xs',
                sm: 'h-9 rounded-md px-3',
                lg: 'h-11 rounded-md px-8',
                xl: 'h-[3.25rem] rounded-md px-6 text-lg',
                icon: 'h-10 w-10',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
    loading?: boolean;
    active?: boolean;
    Icon?: ForwardRefExoticComponent<
        Omit<LucideProps, 'ref'> & RefAttributes<SVGSVGElement>
    >;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size,
            asChild = false,
            loading,
            children,
            disabled,
            active,
            Icon,
            ...props
        },
        ref,
    ) => {
        const Comp = asChild ? Slot : 'button';
        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                disabled={disabled || loading}
                data-state={active ? 'active' : 'inactive'}
                {...props}
            >
                {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                    <>
                        {children}
                        {Icon && <Icon className="h-4 w-4" />}
                    </>
                )}
            </Comp>
        );
    },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
